import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Play,
  PlusCircle,
  Users,
  Activity,
  Crown,
  Settings,
  Volume2,
  VolumeX,
  Globe,
  Lock,
  Unlock,
  AlertTriangle,
  Copy,
  Check,
  LogOut,
  Trophy,
  Medal,
  Home,
  Gift,
  Swords,
  Palette,
  MessageSquare,
  ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { gameApi } from '../api';
import useDominoSocket from '../hooks/useDominoSocket';

// --- Configuration du plateau (14 colonnes x 24 lignes = 336 cases) ---
const BOARD_COLS = 14;
const BOARD_ROWS = 24;
const WIN_CONDITION = 5;
const BET_OPTIONS = [500, 1000, 1500, 2000, 3000];
const MIN_BET = 500;
const COMMISSION_RATE = 0.05;
const TURN_TIME_LIMIT = 35;

const PREDEFINED_MESSAGES = [
  "T'es null 😜",
  'Boom 💥',
  'Yes TIC-TAC-TOE !',
  'Tu sais pas jouer 😂',
  'Trop lent 🐢',
  "M'ap bat ou 😈",
  'Bien joué 👏',
  'Pèlen ! 🪤',
];

const AVATARS = ['🥷', '🎮', '🔱', '🦓', '🔺', '🔵', '💠', '👽', '🤖', '💀'];

const translations = {
  fr: {
    arena: 'ARENA', play_vs_ia: "JOUER VS IA", create_room: 'CRÉER UN SALON', join_room: 'REJOINDRE UN SALON',
    waiting_matches: 'Matchs en attente', live_matches: 'Matchs en direct', stake: 'Mise', pot: 'Pot Total',
    choose_bet: 'Choisissez Votre Mise', custom_bet: 'Mise Personnalisée', bet_too_low: 'Montant trop faible (Min: 500)',
    gains_desc: 'Le vainqueur remporte la mise moins 5% de commission', cancel: 'Annuler', confirm_play: 'Confirmer & Jouer',
    your_bet: 'Votre mise :', opp_bet: 'Mise adverse :', commission: 'Commission (5%) :', you_win_desc: 'Si vous gagnez, vous remporterez :',
    game_over: 'Match Terminé', draw: 'Match Nul', win_msg: 'a Gagné !', play_again: 'Rejouer', menu: 'Menu', quit: 'Quitter la salle',
    quit_warning: 'Si vous quittez ce match, vous perdrez la partie et votre argent. Êtes-vous sûr de vouloir quitter ?',
    yes_quit: 'Oui, quitter', no_stay: 'Non, rester', public: 'Public', private: 'Privé', room_type: 'Type de Salon',
    room_code: "Code d'accès :", share_code: 'Partagez ce code de 6 chiffres avec votre ami', add_to_waiting: 'Créer et Attendre',
    payout: 'Gain', you: 'VOUS', opponent: 'ADVERSAIRE', lobby: 'Lobby', settings: 'Paramètres', user_id: 'ID Joueur',
    wallet: 'Solde', copied: 'Copié !', copy_code: 'Copier le code', win_alert: 'Félicitations !', leaderboard: 'Classement',
    achievements: 'Trophées', home: 'Accueil', rewards: 'Récompenses', tournaments: 'Tournois', coming_soon: 'Bientôt Disponible',
    coming_soon_desc: "Cette section est en cours de développement. Préparez-vous à recevoir des points cadeaux et à participer à nos futurs championnats exclusifs très prochainement !",
    theme_select: 'Style', theme_white: 'Tableau Blanc', theme_dark: 'Tableau Sombre', time_left: 'Temps',
    waiting_turn: 'En attente...', hurry_up: 'Vite! Tour passe dans', live_now: 'EN DIRECT', upcoming: 'À VENIR',
    join_tournament: "S'inscrire", quarter_finals: 'Quarts de Finale', semi_finals: 'Demi-Finales', final: 'Grande Finale',
    best_of: 'Premier à 5 victoires', enter_code: 'Entrez le code du salon', join_btn: 'Rejoindre', invalid_code: 'Code invalide',
    choose_avatar: 'Choisir un Avatar',
  },
  en: {
    arena: 'ARENA', play_vs_ia: 'PLAY VS AI', create_room: 'CREATE A ROOM', join_room: 'JOIN A ROOM',
    waiting_matches: 'Waiting Matches', live_matches: 'Live Matches', stake: 'Stake', pot: 'Total Pot',
    choose_bet: 'Choose Your Bet', custom_bet: 'Custom Bet', bet_too_low: 'Amount too low (Min: 500)',
    gains_desc: 'The winner takes the pot minus 5% commission', cancel: 'Cancel', confirm_play: 'Confirm & Play',
    your_bet: 'Your bet:', opp_bet: "Opponent's bet:", commission: 'Commission (5%):', you_win_desc: 'If you win, you will take:',
    game_over: 'Game Over', draw: 'Draw Match', win_msg: 'has Won!', play_again: 'Play Again', menu: 'Menu', quit: 'Leave Room',
    quit_warning: 'If you leave this match, you will forfeit the game and your money. Are you sure you want to leave?',
    yes_quit: 'Yes, leave', no_stay: 'No, stay', public: 'Public', private: 'Private', room_type: 'Room Type',
    room_code: 'Room Code:', share_code: 'Share this 6-digit code with your friend', add_to_waiting: 'Create & Wait',
    payout: 'Payout', you: 'YOU', opponent: 'OPPONENT', lobby: 'Lobby', settings: 'Settings', user_id: 'Player ID',
    wallet: 'Balance', copied: 'Copied!', copy_code: 'Copy Code', win_alert: 'Congratulations!', leaderboard: 'Leaderboard',
    achievements: 'Achievements', home: 'Home', rewards: 'Rewards', tournaments: 'Tournaments', coming_soon: 'Coming Soon',
    coming_soon_desc: 'This section is currently under development. Get ready to receive gift points and participate in our upcoming exclusive championships very soon!',
    theme_select: 'Style', theme_white: 'White Table', theme_dark: 'Dark Table', time_left: 'Time',
    waiting_turn: 'Waiting...', hurry_up: 'Hurry! Skips in', live_now: 'LIVE NOW', upcoming: 'UPCOMING',
    join_tournament: 'Register', quarter_finals: 'Quarter Finals', semi_finals: 'Semi Finals', final: 'Grand Final',
    best_of: 'First to 5 wins', enter_code: 'Enter room code', join_btn: 'Join', invalid_code: 'Invalid code',
    choose_avatar: 'Choose an Avatar',
  },
  es: {
    arena: 'ARENA', play_vs_ia: 'JUGAR VS IA', create_room: 'CREAR SALA', join_room: 'UNIRSE A SALA',
    waiting_matches: 'Partidas en espera', live_matches: 'Partidas en vivo', stake: 'Apuesta', pot: 'Pozo Total',
    choose_bet: 'Elige Tu Apuesta', custom_bet: 'Apuesta Personalizada', bet_too_low: 'Cantidad muy baja (Min: 500)',
    gains_desc: 'El ganador se lleva el pozo menos 5% de comisión', cancel: 'Cancelar', confirm_play: 'Confirmar y Jugar',
    your_bet: 'Tu apuesta:', opp_bet: 'Apuesta del oponente:', commission: 'Comisión (5%):', you_win_desc: 'Si ganas, te llevarás:',
    game_over: 'Fin de la Partida', draw: 'Empate', win_msg: 'ha Ganado!', play_again: 'Jugar de Nuevo', menu: 'Menú', quit: 'Abandonar Sala',
    quit_warning: 'Si abandonas esta partida, perderás el juego y tu dinero. ¿Estás seguro de que quieres salir?',
    yes_quit: 'Sí, salir', no_stay: 'No, quedarse', public: 'Público', private: 'Privado', room_type: 'Tipo de Sala',
    room_code: 'Código de Sala:', share_code: 'Comparte este código de 6 dígitos con tu amigo', add_to_waiting: 'Crear y Esperar',
    payout: 'Ganancia', you: 'TÚ', opponent: 'OPONENTE', lobby: 'Lobby', settings: 'Ajustes', user_id: 'ID de Jugador',
    wallet: 'Saldo', copied: '¡Copiado!', copy_code: 'Copiar código', win_alert: '¡Felicidades!', leaderboard: 'Clasificación',
    achievements: 'Logros', home: 'Inicio', rewards: 'Recompensas', tournaments: 'Torneos', coming_soon: 'Próximamente',
    coming_soon_desc: 'Esta sección está en desarrollo. ¡Prepárate para recibir puntos de regalo y participar en nuestros futuros campeonatos exclusivos muy pronto!',
    theme_select: 'Estilo', theme_white: 'Tablero Blanco', theme_dark: 'Tablero Oscuro', time_left: 'Tiempo',
    waiting_turn: 'Esperando...', hurry_up: '¡Rápido! Pasa en', live_now: 'EN VIVO', upcoming: 'PRÓXIMO',
    join_tournament: 'Inscribirse', quarter_finals: 'Cuartos de Final', semi_finals: 'Semifinales', final: 'Gran Final',
    best_of: 'El primero a 5 victorias', enter_code: 'Ingrese el código de la sala', join_btn: 'Unirse', invalid_code: 'Código invalido',
    choose_avatar: 'Elige un Avatar',
  },
};

const GlowingX = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={`w-full h-full text-orange-500 drop-shadow-[0_0_5px_rgba(249,115,22,0.8)] ${className}`}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 19L19 5M5 5l14 14" />
  </svg>
);

const GlowingO = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={`w-full h-full text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.8)] ${className}`}>
    <circle cx="12" cy="12" r="8" strokeWidth="3" />
  </svg>
);

const backendCSSPrep = `
  @keyframes borderBlink {
    0%, 100% { border-color: rgba(249, 115, 22, 0.2); box-shadow: 0 0 5px rgba(249, 115, 22, 0.1); }
    50% { border-color: rgba(34, 211, 238, 0.8); box-shadow: 0 0 15px rgba(34, 211, 238, 0.4); }
  }
  .live-match-blink { animation: borderBlink 1.5s ease-in-out infinite; }
  .winner { color: #00ffcc !important; font-weight: 900; text-shadow: 0 0 8px rgba(0, 255, 204, 0.6); }
  .pending { color: #6b7280; font-style: italic; }
  .bracket-line { background: linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.3), rgba(255,255,255,0.1)); height: 2px; }
`;

export default function TicTacToeGame({ onClose }) {
  const { user, refresh } = useAuth();
  const ws = useDominoSocket(true, 'tictactoe');
  const [lang, setLang] = useState('fr');
  const [isMuted, setIsMuted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [boardTheme, setBoardTheme] = useState('dark');
  const [myAvatar, setMyAvatar] = useState('🥷');

  const audioCtxRef = useRef(null);

  const [view, setView] = useState('menu');
  const [gameMode, setGameMode] = useState(null);
  const [showBetModal, setShowBetModal] = useState(false);

  const [selectedBet, setSelectedBet] = useState(500);
  const [currentStake, setCurrentStake] = useState(500);
  const [currentMatchId, setCurrentMatchId] = useState(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [mySymbol, setMySymbol] = useState('X'); // X = host, O = guest in PvP
  const [oppRealName, setOppRealName] = useState(null);
  const [opponentAvatarIcon, setOpponentAvatarIcon] = useState('👽');

  const [roomType, setRoomType] = useState('public');
  const [roomCode, setRoomCode] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [featurePopup, setFeaturePopup] = useState(false);

  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinInput, setJoinInput] = useState('');

  const [chatPopup, setChatPopup] = useState(false);
  const [myBubble, setMyBubble] = useState(null);
  const [oppBubble, setOppBubble] = useState(null);

  const [timeLeft, setTimeLeft] = useState(TURN_TIME_LIMIT);
  const [board, setBoard] = useState(Array(BOARD_COLS * BOARD_ROWS).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState('X');

  const [realWinner, setRealWinner] = useState(null);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [winningCells, setWinningCells] = useState([]);
  const [lastMoveIndex, setLastMoveIndex] = useState(null);

  const [customWaitingMatches, setCustomWaitingMatches] = useState([]);

  const myUsername = user?.username || 'guest';
  const myID = `#${(user?.id || 'temp').slice(-6).toUpperCase()}`;
  const myBalance = Number(user?.walletBalance ?? 0);
  const opponentUsername = gameMode === 'ai' ? 'IA_Bot_v2' : (oppRealName || 'Adversaire');
  const opponentAvatar = gameMode === 'ai' ? '🤖' : opponentAvatarIcon;

  const t = translations[lang];

  useEffect(() => {
    if (view !== 'playing' || realWinner !== null) return;
    setTimeLeft(TURN_TIME_LIMIT);
    const timerInterval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCurrentPlayer((cp) => (cp === 'X' ? 'O' : 'X'));
          return TURN_TIME_LIMIT;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerInterval);
  }, [currentPlayer, view, realWinner, board]);

  const safeToFixed = (val) => {
    const num = Number(val);
    return isNaN(num) ? '0' : num.toFixed(0);
  };

  const playSound = useCallback(
    (type = 'click') => {
      if (isMuted) return;
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const audioCtx = audioCtxRef.current;
        if (audioCtx.state === 'suspended') audioCtx.resume();

        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        if (type === 'click') {
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(320, audioCtx.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(110, audioCtx.currentTime + 0.35);
          gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.35);
          oscillator.start();
          oscillator.stop(audioCtx.currentTime + 0.36);
        } else if (type === 'win') {
          const now = audioCtx.currentTime;
          oscillator.type = 'triangle';
          oscillator.frequency.setValueAtTime(261.63, now);
          oscillator.frequency.setValueAtTime(329.63, now + 0.15);
          oscillator.frequency.setValueAtTime(392.0, now + 0.3);
          oscillator.frequency.setValueAtTime(523.25, now + 0.45);
          gainNode.gain.setValueAtTime(0.25, now);
          gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
          oscillator.start();
          oscillator.stop(now + 0.85);
        }
      } catch (e) {
        console.warn('Audio Context handled safely:', e);
      }
    },
    [isMuted]
  );

  const checkWin = useCallback((currentBoard, lastMove, player) => {
    if (typeof lastMove !== 'number' || lastMove < 0 || lastMove >= currentBoard.length) return null;
    const row = Math.floor(lastMove / BOARD_COLS);
    const col = lastMove % BOARD_COLS;
    const directions = [[0, 1], [1, 0], [1, 1], [1, -1]];
    for (let [dx, dy] of directions) {
      let count = 1;
      let winCells = [lastMove];
      let r = row + dx; let c = col + dy;
      while (r >= 0 && r < BOARD_ROWS && c >= 0 && c < BOARD_COLS) {
        const targetIndex = r * BOARD_COLS + c;
        if (currentBoard[targetIndex] === player) { count++; winCells.push(targetIndex); r += dx; c += dy; } else break;
      }
      r = row - dx; c = col - dy;
      while (r >= 0 && r < BOARD_ROWS && c >= 0 && c < BOARD_COLS) {
        const targetIndex = r * BOARD_COLS + c;
        if (currentBoard[targetIndex] === player) { count++; winCells.push(targetIndex); r -= dx; c -= dy; } else break;
      }
      if (count >= WIN_CONDITION) return winCells.sort((a, b) => a - b);
    }
    return null;
  }, []);

  const settleAiResult = async (resultStr) => {
    if (!currentMatchId) return;
    try {
      await gameApi.settleMatch({ match_id: currentMatchId, result: resultStr });
      await refresh();
    } catch (e) {
      console.warn('Settle failed', e);
    } finally {
      setCurrentMatchId(null);
    }
  };

  const triggerVictory = (player, lineCoords) => {
    setRealWinner(player);
    setWinningCells(lineCoords);
    playSound('win');
    setTimeout(() => { setShowWinnerModal(true); }, 3500);

    if (gameMode === 'ai') {
      const myWin = player === mySymbol;
      settleAiResult(myWin ? 'win' : 'loss');
    } else if (gameMode === 'pvp') {
      const myWin = player === mySymbol;
      ws.endGame({ winner: myWin ? myUsername : opponentUsername });
    }
  };

  const triggerDraw = () => {
    setRealWinner('Egalite');
    setShowWinnerModal(true);
    if (gameMode === 'ai') {
      settleAiResult('tie');
    } else if (gameMode === 'pvp') {
      ws.endGame({ winner: null });
    }
  };

  const handleCellClick = (index) => {
    if (index < 0 || index >= board.length) return;
    if (board[index] || realWinner) return;
    // PvE: block clicks on AI turn
    if (gameMode === 'ai' && currentPlayer === 'O') return;
    // PvP: only allow our symbol's turn
    if (gameMode === 'pvp' && currentPlayer !== mySymbol) return;

    playSound('click');
    const newBoard = [...board];
    newBoard[index] = currentPlayer;
    setBoard(newBoard);
    setLastMoveIndex(index);

    // PvP: relay move to opponent
    if (gameMode === 'pvp') {
      ws.sendMove({ index, symbol: currentPlayer });
    }

    const winLine = checkWin(newBoard, index, currentPlayer);
    if (winLine && winLine.length >= 5) {
      triggerVictory(currentPlayer, winLine);
    } else if (!newBoard.includes(null)) {
      triggerDraw();
    } else {
      setCurrentPlayer(currentPlayer === 'X' ? 'O' : 'X');
    }
  };

  const getAISmartMove = useCallback((currentBoard) => {
    let bestScore = -Infinity;
    let bestMove = -1;
    const emptyCells = [];
    for (let i = 0; i < currentBoard.length; i++) if (currentBoard[i] === null) emptyCells.push(i);
    if (emptyCells.length === currentBoard.length) {
      return Math.floor(BOARD_ROWS / 2) * BOARD_COLS + Math.floor(BOARD_COLS / 2);
    }
    for (const cell of emptyCells) {
      let score = 0;
      const row = Math.floor(cell / BOARD_COLS);
      const col = cell % BOARD_COLS;
      let hasNeighbor = false;
      const radius = 2;
      for (let r = Math.max(0, row - radius); r <= Math.min(BOARD_ROWS - 1, row + radius); r++) {
        for (let c = Math.max(0, col - radius); c <= Math.min(BOARD_COLS - 1, col + radius); c++) {
          if (currentBoard[r * BOARD_COLS + c] !== null) { hasNeighbor = true; break; }
        }
        if (hasNeighbor) break;
      }
      if (!hasNeighbor) continue;

      const directions = [[0, 1], [1, 0], [1, 1], [1, -1]];
      let aiLinesOf3 = 0; let playerLinesOf3 = 0; let aiLinesOf4 = 0; let playerLinesOf4 = 0;
      for (const [dx, dy] of directions) {
        for (let offset = -4; offset <= 0; offset++) {
          let windowO = 0; let windowX = 0; let validWindow = true;
          for (let i = 0; i < 5; i++) {
            const r = row + (offset + i) * dx;
            const c = col + (offset + i) * dy;
            if (r < 0 || r >= BOARD_ROWS || c < 0 || c >= BOARD_COLS) { validWindow = false; break; }
            if (offset + i === 0) continue;
            const val = currentBoard[r * BOARD_COLS + c];
            if (val === 'O') windowO++; else if (val === 'X') windowX++;
          }
          if (validWindow) {
            if (windowO > 0 && windowX > 0) continue;
            if (windowX === 0) {
              if (windowO === 4) { score += 10000000; aiLinesOf4++; }
              else if (windowO === 3) { score += 50000; aiLinesOf3++; }
              else if (windowO === 2) { score += 1000; }
              else if (windowO === 1) { score += 50; }
            }
            if (windowO === 0) {
              if (windowX === 4) { score += 5000000; playerLinesOf4++; }
              else if (windowX === 3) { score += 100000; playerLinesOf3++; }
              else if (windowX === 2) { score += 1200; }
              else if (windowX === 1) { score += 20; }
            }
          }
        }
      }
      if (aiLinesOf3 >= 2) score += 600000;
      if (playerLinesOf3 >= 2) score += 900000;
      if (aiLinesOf4 >= 1 && aiLinesOf3 >= 1) score += 2000000;
      const distFromCenter = Math.abs(row - BOARD_ROWS / 2) + Math.abs(col - BOARD_COLS / 2);
      score += 40 - distFromCenter;
      score += Math.random() * 2;
      if (score > bestScore) { bestScore = score; bestMove = cell; }
    }
    if (bestMove === -1 && emptyCells.length > 0) return emptyCells[Math.floor(Math.random() * emptyCells.length)];
    return bestMove;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gameMode === 'ai' && currentPlayer === 'O' && !realWinner) {
      const timer = setTimeout(() => {
        const aiMove = getAISmartMove(board);
        if (aiMove !== -1) {
          playSound('click');
          const newBoard = [...board];
          newBoard[aiMove] = 'O';
          setBoard(newBoard);
          setLastMoveIndex(aiMove);
          const winLine = checkWin(newBoard, aiMove, 'O');
          if (winLine && winLine.length >= 5) triggerVictory('O', winLine);
          else if (!newBoard.includes(null)) triggerDraw();
          else setCurrentPlayer('X');
        }
      }, 700);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, gameMode, realWinner]);

  const handleCreateRoomBtn = () => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setRoomCode(code);
    setGameMode('pvp');
    setRoomType('public');
    setShowBetModal(true);
  };

  const handleJoinSubmit = async () => {
    if (joinInput.length < 4) return;
    setShowJoinModal(false);
    setGameMode('pvp');
    setRoomType('private');
    setMySymbol('O');
    const res = await ws.joinRoom({ room_id: joinInput, avatar: myAvatar });
    if (!res?.ok) {
      alert(t.invalid_code + (res?.error ? ` (${res.error})` : ''));
      setGameMode(null);
      return;
    }
    setCurrentStake(Number(res.room?.bet || 500));
    setOppRealName(res.room?.host_username || null);
    setOpponentAvatarIcon(res.room?.host_avatar || '👽');
    // Don't enter playing view yet — wait for ws.matchStarted (after both players ready)
    setWaitingForOpponent(true);
  };

  const startAiMatch = async (stakeAmount) => {
    try {
      const r = await gameApi.startMatch({ bet: stakeAmount, game: 'tictactoe' });
      setCurrentMatchId(r.match_id);
      setCurrentStake(stakeAmount);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || 'Insufficient balance');
      return false;
    }
    setBoard(Array(BOARD_COLS * BOARD_ROWS).fill(null));
    setCurrentPlayer('X');
    setRealWinner(null);
    setShowWinnerModal(false);
    setWinningCells([]);
    setLastMoveIndex(null);
    setMySymbol('X');
    setView('playing');
    return true;
  };

  const confirmAndStart = async () => {
    const stakeAmount = Number(selectedBet || 500);
    if (stakeAmount < MIN_BET) return;
    if (stakeAmount > myBalance) {
      alert(lang === 'fr' ? `Solde insuffisant. Achetez du DOMIA en présale.` : `Insufficient balance. Buy DOMIA in the presale.`);
      return;
    }
    setShowBetModal(false);

    // === MULTIPLAYER ===
    if (gameMode === 'pvp') {
      const res = await ws.createRoom({ type: roomType, bet: stakeAmount, avatar: myAvatar, game: 'tictactoe' });
      if (!res?.ok) {
        alert(res?.error || 'Failed to create room');
        return;
      }
      setCurrentStake(stakeAmount);
      setMySymbol('X'); // host
      setWaitingForOpponent(true);
      setRoomCode(res.room_id);
      return;
    }

    // === SINGLE PLAYER vs AI ===
    await startAiMatch(stakeAmount);
  };

  const handleQuitGameAction = () => setShowQuitModal(true);

  const confirmQuitMatch = async () => {
    setShowQuitModal(false);
    if (gameMode === 'ai' && currentMatchId) {
      // Forfeit = treat as loss
      await settleAiResult('loss');
    } else if (gameMode === 'pvp') {
      // Forfeit PvP: the other player wins
      ws.endGame({ winner: opponentUsername });
      try { await ws.leaveRoom(); } catch (e) { /* ignore */ }
    }
    setView('menu');
    setWaitingForOpponent(false);
  };

  // === Socket.IO listeners (PvP) ===
  useEffect(() => {
    if (!ws.matchStarted) return;
    // Both players ready, start the game
    const payload = ws.matchStarted;
    setBoard(Array(BOARD_COLS * BOARD_ROWS).fill(null));
    setCurrentPlayer('X'); // X always plays first
    setRealWinner(null);
    setShowWinnerModal(false);
    setWinningCells([]);
    setLastMoveIndex(null);
    // Sync stake from server
    if (payload.bet) setCurrentStake(Number(payload.bet));
    // Refresh balance (server debited)
    refresh();
    setWaitingForOpponent(false);
    setView('playing');
    ws.clearMatchStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.matchStarted]);

  useEffect(() => {
    if (!ws.incomingMove) return;
    const { move } = ws.incomingMove;
    if (!move || typeof move.index !== 'number') {
      ws.clearIncomingMove();
      return;
    }
    setBoard((prev) => {
      if (prev[move.index] !== null) return prev;
      const nb = [...prev];
      nb[move.index] = move.symbol;
      // Detect win/draw locally too
      const wl = checkWin(nb, move.index, move.symbol);
      setLastMoveIndex(move.index);
      if (wl && wl.length >= 5) {
        setRealWinner(move.symbol);
        setWinningCells(wl);
        playSound('win');
        setTimeout(() => setShowWinnerModal(true), 3500);
      } else if (!nb.includes(null)) {
        setRealWinner('Egalite');
        setShowWinnerModal(true);
      } else {
        setCurrentPlayer(move.symbol === 'X' ? 'O' : 'X');
      }
      return nb;
    });
    ws.clearIncomingMove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.incomingMove]);

  useEffect(() => {
    if (!ws.matchEnded) return;
    // Server settled — refresh balance
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.matchEnded]);

  const copyToClipboard = () => {
    const el = document.createElement('textarea');
    el.value = roomCode;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const getWinLineCoords = () => {
    if (!realWinner || realWinner === 'Egalite') return null;
    if (!winningCells || !Array.isArray(winningCells) || winningCells.length < 5) return null;
    const first = winningCells[0];
    const last = winningCells[winningCells.length - 1];
    if (typeof first !== 'number' || typeof last !== 'number') return null;
    const x1 = ((first % BOARD_COLS) + 0.5) * (100 / BOARD_COLS);
    const y1 = (Math.floor(first / BOARD_COLS) + 0.5) * (100 / BOARD_ROWS);
    const x2 = ((last % BOARD_COLS) + 0.5) * (100 / BOARD_COLS);
    const y2 = (Math.floor(last / BOARD_COLS) + 0.5) * (100 / BOARD_ROWS);
    if (!isFinite(x1) || !isFinite(y1) || !isFinite(x2) || !isFinite(y2)) return null;
    return { x1: `${x1}%`, y1: `${y1}%`, x2: `${x2}%`, y2: `${y2}%` };
  };

  const winLineCoords = getWinLineCoords();

  const registerTournament = (tournamentId) => alert(`Tournament Registered!\nTournament ID: ${tournamentId}`);

  const handleSendChat = (msg) => {
    setMyBubble(msg);
    setChatPopup(false);
    setTimeout(() => setMyBubble(null), 3000);
    if (gameMode === 'ai' && Math.random() > 0.3) {
      setTimeout(() => {
        const aiReplies = ['Boom 💥', 'Tu sais pas jouer 😂', '🤔', "T'es null 😜", 'Pèlen ! 🪤'];
        setOppBubble(aiReplies[Math.floor(Math.random() * aiReplies.length)]);
        setTimeout(() => setOppBubble(null), 3000);
      }, 1500);
    }
  };

  /* eslint-disable-next-line react/no-unstable-nested-components */
  const ParticleBackground = () => (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#0d121c]">
      <style>{backendCSSPrep}</style>
      <div className="absolute top-[-20%] left-[-15%] w-[60%] h-[60%] rounded-full bg-orange-900/10 blur-[150px]"></div>
      <div className="absolute bottom-[-10%] right-[-15%] w-[60%] h-[60%] rounded-full bg-cyan-900/10 blur-[150px]"></div>
    </div>
  );

  const renderJoinModal = () => {
    if (!showJoinModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm px-4 animate-in fade-in duration-200">
        <div className="bg-[#171f30] border border-white/10 w-full max-w-sm rounded-2xl shadow-2xl p-6 relative">
          <h3 className="text-lg font-bold text-white mb-4 text-center uppercase tracking-wider">{t.join_room}</h3>
          <div className="mb-6">
            <label className="text-xs font-bold text-gray-400 mb-2 block">{t.enter_code}</label>
            <input
              type="text"
              value={joinInput}
              onChange={(e) => setJoinInput(e.target.value.toUpperCase())}
              placeholder="EX: 882103"
              maxLength={6}
              data-testid="ttt-join-code-input"
              className="w-full bg-[#0d121c] border border-white/10 rounded-xl px-4 py-3 text-center text-white font-mono font-bold text-xl tracking-widest focus:border-cyan-500 focus:outline-none transition-colors"
            />
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowJoinModal(false)} className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium transition-colors">{t.cancel}</button>
            <button
              onClick={handleJoinSubmit}
              disabled={joinInput.length < 4}
              data-testid="ttt-confirm-join-btn"
              className={`flex-1 py-3 rounded-xl font-bold shadow-lg transition-opacity uppercase text-white ${joinInput.length >= 4 ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-gray-600 opacity-50 cursor-not-allowed'}`}
            >
              {t.join_btn}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSettingsDropdown = () => {
    if (!showSettings) return null;
    return (
      <div className="absolute top-16 right-4 z-40 w-72 bg-[#171f30] border border-white/10 rounded-2xl p-5 shadow-[0_15px_30px_rgba(0,0,0,0.5)] animate-in fade-in slide-in-from-top-3 duration-200">
        <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-3 border-b border-white/5 pb-2">{t.settings}</h4>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-400">{t.user_id}</span>
            <span className="text-xs text-white font-mono font-bold bg-white/5 px-2 py-1 rounded">{myUsername} ({myID})</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-400">{t.wallet}</span>
            <span className="text-xs text-yellow-400 font-bold bg-yellow-400/10 border border-yellow-500/20 px-2 py-1 rounded flex items-center gap-1">{safeToFixed(myBalance)} DOMIA</span>
          </div>

          <div className="border-t border-white/5 pt-3">
            <span className="text-xs text-gray-400 block mb-2">{t.choose_avatar}</span>
            <div className="flex flex-wrap gap-2 justify-center">
              {AVATARS.map((a) => (
                <button
                  key={a}
                  onClick={() => setMyAvatar(a)}
                  className={`text-xl p-1.5 rounded-lg transition-all ${myAvatar === a ? 'bg-orange-500/20 border border-orange-500 scale-110 shadow-[0_0_10px_rgba(249,115,22,0.3)]' : 'bg-white/5 border border-transparent hover:bg-white/10'}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-white/5 pt-3 flex justify-between items-center">
            <span className="text-xs text-gray-400">Audio Synth</span>
            <button onClick={() => setIsMuted(!isMuted)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300">
              {isMuted ? <VolumeX className="w-4 h-4 text-red-400" /> : <Volume2 className="w-4 h-4 text-green-400" />}
            </button>
          </div>

          {view === 'playing' && (
            <div className="border-t border-white/5 pt-3">
              <button
                onClick={() => { setShowSettings(false); handleQuitGameAction(); }}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-red-600/20 hover:bg-red-600 border border-red-500/30 text-white font-bold text-xs transition-all"
              >
                <LogOut className="w-4 h-4" />
                {t.quit}
              </button>
            </div>
          )}

          <div className="border-t border-white/5 pt-3">
            <button
              onClick={() => { setShowSettings(false); if (typeof onClose === 'function') onClose(); }}
              data-testid="ttt-back-to-presale"
              className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/40 border border-amber-500/30 text-amber-200 font-bold text-xs transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              {lang === 'fr' ? 'Retour à la Présale' : lang === 'en' ? 'Back to Presale' : 'Volver al Presale'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderBetModal = () => {
    if (!showBetModal) return null;
    const betAmount = Number(selectedBet || 0);
    const isValidBet = betAmount >= MIN_BET;
    const potTotal = betAmount * 2;
    const siteCommission = potTotal * COMMISSION_RATE;
    const winnerGets = potTotal - siteCommission;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm px-4 py-4 animate-in fade-in duration-200">
        <div className="bg-[#171f30] border border-white/10 w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
          <div className="bg-gradient-to-r from-orange-900/40 to-cyan-900/40 p-3 border-b border-white/5 text-center shrink-0">
            <h3 className="text-lg font-bold text-white uppercase tracking-wider">{t.choose_bet}</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">{t.gains_desc}</p>
          </div>
          <div className="p-4 space-y-3 overflow-y-auto scrollbar-hide">
            <div className="grid grid-cols-3 gap-2">
              {BET_OPTIONS.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setSelectedBet(amount)}
                  data-testid={`ttt-bet-option-${amount}`}
                  className={`py-1.5 rounded-xl border flex flex-col items-center justify-center transition-all ${selectedBet === amount ? 'bg-yellow-500/20 border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.2)] text-yellow-400' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'}`}
                >
                  <span className="font-bold text-sm">{amount}</span>
                  <span className="text-[9px]">DOMIA</span>
                </button>
              ))}
            </div>

            <div className="bg-black/30 p-2.5 rounded-xl border border-white/5 space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-gray-400 uppercase font-bold">{t.custom_bet}</span>
              </div>
              <div className="flex gap-2 h-9">
                <input
                  type="number"
                  min="500"
                  className="flex-1 bg-[#0d121c] border border-white/10 rounded-lg px-3 text-white font-bold text-sm focus:border-orange-500 focus:outline-none"
                  placeholder="500..."
                  value={selectedBet === 0 ? '' : selectedBet}
                  onChange={(e) => setSelectedBet(Number(e.target.value))}
                />
                <button onClick={() => setSelectedBet(myBalance)} className="bg-white/10 hover:bg-white/20 text-white font-bold px-3 rounded-lg text-xs transition-colors">MAX</button>
              </div>
              {!isValidBet && selectedBet > 0 && (
                <p className="text-red-500 text-[10px] font-bold animate-pulse">{t.bet_too_low}</p>
              )}
            </div>

            {gameMode === 'pvp' && (
              <div className="bg-white/5 p-3 rounded-xl border border-white/5 space-y-2">
                <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest block">{t.room_type}</span>
                <div className="flex gap-2">
                  <button onClick={() => setRoomType('public')} className={`flex-1 py-1.5 px-2 rounded-lg border flex items-center justify-center gap-1.5 text-xs transition-all ${roomType === 'public' ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/5 bg-black/20 text-gray-400'}`}>
                    <Unlock className="w-3 h-3" /> {t.public}
                  </button>
                  <button onClick={() => setRoomType('private')} className={`flex-1 py-1.5 px-2 rounded-lg border flex items-center justify-center gap-1.5 text-xs transition-all ${roomType === 'private' ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400' : 'border-white/5 bg-black/20 text-gray-400'}`}>
                    <Lock className="w-3 h-3" /> {t.private}
                  </button>
                </div>

                {roomType === 'private' && (
                  <div className="bg-black/30 p-2.5 rounded-lg border border-cyan-500/30 text-center space-y-1.5 mt-1">
                    <span className="text-[10px] text-cyan-400 font-bold block">{t.room_code}</span>
                    <div className="flex items-center justify-center gap-2 bg-black/50 p-1.5 rounded border border-white/10">
                      <span className="text-lg font-mono font-black tracking-widest text-white">{roomCode}</span>
                      <button onClick={copyToClipboard} className="p-1 hover:bg-white/10 text-gray-400 rounded transition-colors">
                        {copiedCode ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-[#0d121c] rounded-xl p-3 border border-white/5 space-y-2">
              <div className="flex justify-between items-center text-[11px] text-gray-400">
                <span>{t.your_bet}</span>
                <span className="text-white font-medium">{safeToFixed(betAmount)} DOMIA</span>
              </div>
              <div className="flex justify-between items-center text-[11px] text-gray-400">
                <span>{t.opp_bet}</span>
                <span className="text-white font-medium">{safeToFixed(betAmount)} DOMIA</span>
              </div>
              <div className="w-full h-[1px] bg-white/5"></div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400 font-medium">{t.pot} :</span>
                <span className="text-yellow-500 font-black">{safeToFixed(potTotal)} DOMIA</span>
              </div>
              <div className="flex justify-between items-center text-[11px] text-gray-500">
                <span>{t.commission}</span>
                <span className="text-red-400">-{safeToFixed(siteCommission)} DOMIA</span>
              </div>
              <div className="w-full h-[1px] bg-white/5"></div>
              <div className="flex justify-between items-center">
                <span className="text-gray-200 font-bold text-[11px]">{t.you_win_desc}</span>
                <span className="text-green-400 font-black text-base">{safeToFixed(winnerGets)} DOMIA</span>
              </div>
            </div>

            <div className="flex gap-2 pt-1 shrink-0">
              <button onClick={() => setShowBetModal(false)} className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium text-xs transition-colors">{t.cancel}</button>
              <button
                disabled={!isValidBet}
                onClick={confirmAndStart}
                data-testid="ttt-confirm-start-btn"
                className={`flex-[2] py-2.5 rounded-xl font-bold shadow-lg transition-opacity uppercase tracking-wider text-xs ${isValidBet ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:opacity-90 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
              >
                {roomType === 'private' ? t.add_to_waiting : t.confirm_play}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderQuitModal = () => {
    if (!showQuitModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm px-4 animate-in fade-in duration-200">
        <div className="bg-[#171f30] border border-red-500/30 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6 text-center space-y-4">
            <div className="w-12 h-12 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center mx-auto text-red-500 animate-bounce">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-black text-white uppercase tracking-wider">{t.quit}?</h3>
            <p className="text-sm text-gray-400 leading-relaxed">{t.quit_warning}</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowQuitModal(false)} className="flex-1 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white font-medium text-sm transition-colors">{t.no_stay}</button>
              <button onClick={confirmQuitMatch} className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm shadow-lg transition-colors">{t.yes_quit}</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFeaturePopup = () => {
    if (!featurePopup) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 animate-in fade-in duration-200">
        <div className="bg-[#171f30] border border-white/10 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6 text-center space-y-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full p-1 mx-auto shadow-[0_0_15px_rgba(234,179,8,0.3)]">
              <div className="w-full h-full bg-[#121826] rounded-full flex items-center justify-center">
                <Gift className="w-8 h-8 text-yellow-500" />
              </div>
            </div>
            <h3 className="text-xl font-black text-white uppercase tracking-wider">{t.coming_soon}</h3>
            <p className="text-sm text-gray-400 leading-relaxed">{t.coming_soon_desc}</p>
            <button onClick={() => setFeaturePopup(false)} className="w-full mt-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-sm transition-colors">OK</button>
          </div>
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <header className="relative z-30 w-full p-4 flex items-center justify-between border-b border-white/5 bg-[#0d121c]/90 backdrop-blur-md">
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          data-testid="ttt-exit-game"
          className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
          title={lang === 'fr' ? 'Retour' : 'Back'}
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 border border-white/10 shadow-sm">
          <div className="flex -space-x-1">
            <span className="text-orange-500 font-bold text-base">X</span>
            <span className="text-cyan-400 font-bold text-base">O</span>
          </div>
        </div>
        <div>
          <h1 className="text-lg font-black tracking-widest text-white uppercase">Arena</h1>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1.5 rounded-full border border-white/10 text-xs font-semibold text-gray-300">
          <Globe className="w-3.5 h-3.5 text-gray-400" />
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="bg-transparent border-none outline-none focus:ring-0 text-gray-200 cursor-pointer">
            <option value="fr" className="bg-[#171f30]">FR</option>
            <option value="en" className="bg-[#171f30]">EN</option>
            <option value="es" className="bg-[#171f30]">ES</option>
          </select>
        </div>
        <button onClick={() => setShowSettings(!showSettings)} className={`p-2 rounded-full border transition-all ${showSettings ? 'bg-orange-500/20 border-orange-500 text-orange-400' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {renderSettingsDropdown()}
    </header>
  );

  const renderLobbyTabBar = () => (
    <div className="fixed bottom-0 left-0 w-full bg-[#0d121c]/95 backdrop-blur-md border-t border-white/10 z-40 px-2 py-3 flex justify-around items-center">
      <button onClick={() => setFeaturePopup(true)} className="flex flex-col items-center gap-1 transition-colors text-gray-500 hover:text-yellow-500">
        <Trophy className="w-5 h-5" /><span className="text-[9px] font-bold uppercase">{t.leaderboard}</span>
      </button>
      <button onClick={() => setFeaturePopup(true)} className="flex flex-col items-center gap-1 transition-colors text-gray-500 hover:text-yellow-500">
        <Medal className="w-5 h-5" /><span className="text-[9px] font-bold uppercase">{t.achievements}</span>
      </button>
      <button onClick={() => setView('menu')} className={`flex flex-col items-center gap-1 transform -translate-y-2 p-3 rounded-xl border shadow-[0_0_15px_rgba(249,115,22,0.2)] transition-all ${view === 'menu' ? 'text-orange-500 bg-[#121826] border-orange-500/30' : 'text-gray-400 bg-[#171f30] border-white/10'}`}>
        <Home className="w-6 h-6" /><span className="text-[10px] font-black uppercase">{t.home}</span>
      </button>
      <button onClick={() => setFeaturePopup(true)} className="relative flex flex-col items-center gap-1 transition-colors text-gray-500 hover:text-yellow-500">
        <Gift className="w-5 h-5" />
        <span className="absolute -top-1 -right-2 w-4 h-4 bg-red-500 text-white text-[9px] font-bold flex items-center justify-center rounded-full border border-[#0d121c]">1</span>
        <span className="text-[9px] font-bold uppercase">{t.rewards}</span>
      </button>
      <button onClick={() => setView('tournaments')} className={`flex flex-col items-center gap-1 transition-colors ${view === 'tournaments' ? 'text-orange-500' : 'text-gray-500 hover:text-yellow-500'}`}>
        <Swords className="w-5 h-5" /><span className="text-[9px] font-bold uppercase">{t.tournaments}</span>
      </button>
    </div>
  );

  const renderMainMenu = () => (
    <div className="relative z-10 flex flex-col items-center w-full max-w-md mx-auto p-6 space-y-8 animate-in fade-in duration-500 pb-28">
      <div className="text-center mt-2 flex flex-col items-center">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-[2px] mb-4 shadow-[0_0_20px_rgba(249,115,22,0.25)]">
          <div className="w-full h-full bg-[#121826] rounded-xl flex items-center justify-center">
            <Crown className="w-8 h-8 text-orange-500" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-gray-400 tracking-[0.2em] uppercase">Tic-Tac-Toe</h2>
        <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500 tracking-tight">{t.arena}</h1>
        <div className="mt-3 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-3 py-1.5 rounded-full">
          <span className="text-[9px] uppercase tracking-widest text-yellow-300/70 font-bold">{t.wallet}</span>
          <span className="text-yellow-400 font-black text-sm" data-testid="ttt-balance">{safeToFixed(myBalance)} DOMIA</span>
        </div>
        {myBalance < MIN_BET && (
          <button
            onClick={onClose}
            data-testid="ttt-buy-domia-btn"
            className="mt-3 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-700 text-white font-black text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(245,158,11,0.5)] hover:opacity-90"
          >
            {lang === 'fr' ? '💎 Acheter du DOMIA (Présale)' : lang === 'en' ? '💎 Buy DOMIA (Presale)' : '💎 Comprar DOMIA (Presale)'}
          </button>
        )}
      </div>

      <div className="w-full space-y-4">
        <button onClick={() => { setGameMode('ai'); setShowBetModal(true); }} data-testid="ttt-play-ai-btn" className="w-full relative group overflow-hidden rounded-xl p-[1px]">
          <span className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-500 rounded-xl opacity-80 group-hover:opacity-100 transition-opacity duration-300"></span>
          <div className="relative flex items-center justify-center gap-3 bg-[#171f30] px-6 py-4 rounded-xl group-hover:bg-transparent transition-colors duration-300">
            <Play className="w-5 h-5 text-orange-400 group-hover:text-white transition-colors" />
            <span className="font-bold text-sm text-white tracking-wide uppercase">{t.play_vs_ia}</span>
          </div>
        </button>

        <button onClick={() => { try { toast({ title: lang === 'fr' ? '🚧 Multijoueur en préparation' : '🚧 Multiplayer coming soon', description: lang === 'fr' ? 'Reviens bientôt — la fonctionnalité arrive !' : 'Stay tuned!' }); } catch (_) { /* noop */ } }} data-testid="ttt-create-room-btn" className="w-full relative group overflow-hidden rounded-xl p-[1px] opacity-60 cursor-not-allowed">
          <span className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-500 rounded-xl opacity-60"></span>
          <div className="relative flex items-center justify-center gap-3 bg-[#171f30] px-6 py-4 rounded-xl">
            <PlusCircle className="w-5 h-5 text-cyan-400" />
            <span className="font-bold text-sm text-white tracking-wide uppercase">{t.create_room}</span>
            <span className="text-[9px] font-black bg-yellow-500 text-black px-1.5 py-0.5 rounded uppercase ml-2">{lang === 'fr' ? 'Bientôt' : 'Soon'}</span>
          </div>
        </button>

        <button onClick={() => { try { toast({ title: lang === 'fr' ? '🚧 Multijoueur en préparation' : '🚧 Multiplayer coming soon', description: lang === 'fr' ? 'Reviens bientôt — la fonctionnalité arrive !' : 'Stay tuned!' }); } catch (_) { /* noop */ } }} data-testid="ttt-join-room-btn" className="w-full relative group overflow-hidden rounded-xl p-[1px] opacity-60 cursor-not-allowed">
          <span className="absolute inset-0 bg-gradient-to-r from-gray-600 to-gray-500 rounded-xl opacity-50"></span>
          <div className="relative flex items-center justify-center gap-3 bg-[#171f30] px-6 py-4 rounded-xl">
            <Users className="w-5 h-5 text-gray-400" />
            <span className="font-bold text-sm text-white tracking-wide uppercase">{t.join_room}</span>
            <span className="text-[9px] font-black bg-yellow-500 text-black px-1.5 py-0.5 rounded uppercase ml-2">{lang === 'fr' ? 'Bientôt' : 'Soon'}</span>
          </div>
        </button>
      </div>

      <div className="w-full space-y-4">
        <div className="flex items-center gap-2 mb-1 px-1">
          <Activity className="w-4 h-4 text-orange-400" />
          <h3 className="text-xs font-bold text-gray-300 uppercase tracking-widest">{t.live_matches} ({ws.lobby.length})</h3>
          <span className={`ml-auto text-[9px] font-bold uppercase ${ws.connected ? 'text-emerald-400' : 'text-gray-500'}`}>
            {ws.connected ? '● LIVE' : '● OFFLINE'}
          </span>
        </div>
        <div className="space-y-2" data-testid="ttt-live-rooms">
          {ws.lobby.length === 0 && customWaitingMatches.length === 0 && (
            <div className="text-center py-6 text-gray-500 text-xs italic">
              {lang === 'fr' ? 'Aucune partie en attente.' : lang === 'en' ? 'No matches waiting.' : 'Sin partidas en espera.'}
            </div>
          )}
          {ws.lobby.map((r) => (
            <button
              key={r.id}
              onClick={async () => {
                if (myBalance < Number(r.bet || 0)) {
                  alert(lang === 'fr' ? 'Solde insuffisant. Achetez du DOMIA.' : 'Insufficient balance. Buy DOMIA.');
                  return;
                }
                setGameMode('pvp');
                setRoomType('public');
                setMySymbol('O');
                const res = await ws.joinRoom({ room_id: r.id, avatar: myAvatar });
                if (!res?.ok) {
                  alert(t.invalid_code + (res?.error ? ` (${res.error})` : ''));
                  setGameMode(null);
                  return;
                }
                setCurrentStake(Number(r.bet || 500));
                setOppRealName(r.host_username);
                setOpponentAvatarIcon(r.host_avatar || '👽');
                setWaitingForOpponent(true);
              }}
              data-testid={`ttt-room-${r.id}`}
              className="w-full flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/5 hover:border-orange-500/40 transition-all cursor-pointer"
            >
              <div className="flex flex-col space-y-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-bold">#{r.id}</span>
                  <span className="bg-green-500/10 border border-green-500/30 text-green-400 text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1"><Unlock className="w-2.5 h-2.5" /> {t.public}</span>
                </div>
                <span className="text-[11px] text-gray-400 font-medium">Host: {r.host_avatar} {r.host_username}</span>
              </div>
              <div className="flex flex-col items-end space-y-1">
                <span className="text-xs font-bold text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded border border-yellow-400/10">{r.bet} DOMIA</span>
                <span className="text-[9px] text-emerald-400 font-bold">{t.join_btn} →</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTournamentsView = () => (
    <div id="tournaments-view" className="relative z-10 flex flex-col w-full max-w-lg mx-auto p-4 space-y-6 animate-in fade-in duration-500 pb-28">
      <div className="text-center mt-2 flex flex-col items-center">
        <Swords className="w-12 h-12 text-orange-500 mb-2 drop-shadow-[0_0_10px_rgba(249,115,22,0.8)]" />
        <h1 className="text-3xl font-black text-white tracking-tight uppercase">{t.tournaments}</h1>
        <p className="text-xs text-gray-400 tracking-widest uppercase mt-1">Arena Championship</p>
      </div>

      <div className="space-y-4">
        <div className="bg-[#171f30] rounded-2xl border border-orange-500/30 overflow-hidden shadow-lg live-match-blink relative">
          <div className="absolute top-3 right-3 bg-red-600 text-white text-[9px] font-bold px-2 py-1 rounded animate-pulse">{t.live_now}</div>
          <div className="p-5">
            <h3 className="text-xl font-black text-white">CYBER SPLIT #01</h3>
            <p className="text-sm text-gray-400 mb-4">{t.best_of} (Connect-5)</p>
            <div className="flex justify-between items-center bg-[#0d121c] p-3 rounded-xl border border-white/5">
              <div>
                <span className="block text-[10px] text-gray-500 uppercase font-bold">Prize Pool</span>
                <span className="text-lg font-black text-yellow-400">50,000 DOMIA</span>
              </div>
              <button onClick={() => registerTournament('cyber_split_01')} className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-2 px-5 rounded-lg text-xs transition-colors shadow-[0_0_10px_rgba(249,115,22,0.4)]">{t.join_tournament}</button>
            </div>
          </div>
        </div>

        <div className="bg-[#121826] rounded-2xl border border-white/10 overflow-hidden shadow-lg opacity-70">
          <div className="p-5">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-bold text-gray-300">WINTER CLASH &apos;26</h3>
              <span className="bg-gray-700 text-gray-300 text-[9px] font-bold px-2 py-1 rounded">{t.upcoming}</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">Starts in 3 days</p>
            <div className="flex justify-between items-center bg-[#0d121c] p-3 rounded-xl border border-white/5">
              <div>
                <span className="block text-[10px] text-gray-600 uppercase font-bold">Prize Pool</span>
                <span className="text-sm font-bold text-gray-400">100,000 DOMIA</span>
              </div>
              <button disabled className="bg-gray-700 text-gray-400 font-bold py-2 px-5 rounded-lg text-xs cursor-not-allowed">{t.join_tournament}</button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-[#171f30] rounded-2xl p-4 border border-white/10 shadow-lg">
        <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4 border-b border-white/5 pb-2">Cyber Split Bracket - {t.quarter_finals}</h4>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-[#0d121c] rounded-xl border border-cyan-500/40 p-3 live-match-blink">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-white">Player_X88</span>
                <span className="text-sm font-black text-cyan-400 bg-cyan-400/10 px-2 py-0.5 rounded">4</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-white">Shadow_Kiid</span>
                <span className="text-sm font-black text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded">3</span>
              </div>
            </div>
            <div className="w-8 bracket-line"></div>
            <div className="w-16 h-10 border-l-2 border-t-2 border-white/20 rounded-tl-lg"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-[#0d121c] rounded-xl border border-white/5 p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold winner">{myUsername}</span>
                <span className="text-sm font-black text-green-400 bg-green-400/10 px-2 py-0.5 rounded">5</span>
              </div>
              <div className="flex justify-between items-center opacity-50">
                <span className="text-xs font-bold text-gray-400 line-through">CryptoRebel</span>
                <span className="text-sm font-black text-gray-500 bg-gray-800 px-2 py-0.5 rounded">2</span>
              </div>
            </div>
            <div className="w-8 bracket-line"></div>
            <div className="w-16 h-10 border-l-2 border-b-2 border-white/20 rounded-bl-lg transform -translate-y-10"></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGameArea = () => {
    const stakeVal = Number(currentStake || 0);
    const totalPot = stakeVal * 2;
    const siteCommission = totalPot * COMMISSION_RATE;
    const winnerGets = totalPot - siteCommission;

    return (
      <div className="relative z-10 flex flex-col items-center w-full max-w-[500px] mx-auto p-2 animate-in fade-in duration-300 pb-20">
        <div className="w-full flex justify-between items-center mb-2 px-1">
          <div className="flex items-center gap-2 bg-[#171f30]/80 px-2.5 py-1 rounded-lg border border-white/5 relative z-[60]">
            <Palette className="w-3.5 h-3.5 text-orange-400" />
            <select value={boardTheme} onChange={(e) => setBoardTheme(e.target.value)} className="bg-transparent border-none outline-none focus:ring-0 text-[10px] font-bold text-gray-300 cursor-pointer uppercase">
              <option value="white" className="bg-[#171f30]">{t.theme_white}</option>
              <option value="dark" className="bg-[#171f30]">{t.theme_dark}</option>
            </select>
            <div className="w-[1px] h-4 bg-white/20 mx-1"></div>
            <div className="relative">
              <button onClick={() => setChatPopup(!chatPopup)} className="p-1 hover:bg-white/10 rounded-full transition-colors relative">
                <MessageSquare className="w-4 h-4 text-cyan-400" />
              </button>
              {chatPopup && (
                <div className="absolute top-full mt-2 left-0 w-48 bg-[#121826] border border-cyan-500/30 rounded-xl shadow-2xl p-2 z-[9999] animate-in fade-in zoom-in-95 duration-200">
                  {PREDEFINED_MESSAGES.map((msg, i) => (
                    <button key={i} onClick={() => handleSendChat(msg)} className="w-full text-left text-[11px] font-bold text-gray-300 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors">{msg}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className="text-orange-400 text-[10px] uppercase tracking-[0.2em] font-black animate-pulse">Connect 5</span>
          </div>
        </div>

        <div className="w-full flex items-center justify-between bg-[#171f30] px-3 py-2 rounded-xl border border-white/5 mb-3 shadow-lg relative z-[50]">
          {currentPlayer === 'X' && timeLeft <= 10 && !realWinner && (
            <div className="absolute -top-7 left-4 bg-red-600 text-white text-[9px] px-2 py-1 rounded-md animate-bounce shadow-lg font-bold">{t.hurry_up} {timeLeft}s!</div>
          )}
          {currentPlayer === 'O' && timeLeft <= 10 && !realWinner && (
            <div className="absolute -top-7 right-4 bg-red-600 text-white text-[9px] px-2 py-1 rounded-md animate-bounce shadow-lg font-bold">{t.hurry_up} {timeLeft}s!</div>
          )}

          <div className={`flex items-center gap-2 transition-all duration-300 relative ${currentPlayer === 'X' ? 'opacity-100 scale-105' : 'opacity-40'}`}>
            {myBubble && (
              <div className="absolute bottom-full mb-2 left-0 bg-white text-black text-[12px] font-bold px-3 py-1.5 rounded-xl rounded-bl-sm shadow-xl whitespace-nowrap z-[9999] animate-in fade-in slide-in-from-bottom-2">{myBubble}</div>
            )}
            <div className={`relative w-8 h-8 rounded-lg flex items-center justify-center bg-[#0d121c] border ${currentPlayer === 'X' ? 'border-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.4)]' : 'border-[#2d3748]'} text-lg`}>{myAvatar}</div>
            <div className="flex flex-col">
              <span className="text-[11px] font-black text-white">{myUsername}</span>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-gray-500 text-[8px] uppercase font-bold">{t.time_left}</span>
                <span className={`text-[10px] font-bold ${currentPlayer === 'X' ? (timeLeft <= 10 ? 'text-red-500' : 'text-orange-400') : 'text-gray-600'}`}>
                  {currentPlayer === 'X' ? `${timeLeft}s` : t.waiting_turn}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <div className="bg-yellow-500/10 border border-yellow-500/20 px-2.5 py-1 rounded-md">
              <span className="text-xs font-black text-yellow-500">{safeToFixed(totalPot)} DOMIA</span>
            </div>
          </div>

          <div className={`flex items-center gap-2 transition-all duration-300 relative ${currentPlayer === 'O' ? 'opacity-100 scale-105' : 'opacity-40'}`}>
            {oppBubble && (
              <div className="absolute bottom-full mb-2 right-0 bg-white text-black text-[12px] font-bold px-3 py-1.5 rounded-xl rounded-br-sm shadow-xl whitespace-nowrap z-[9999] animate-in fade-in slide-in-from-bottom-2">{oppBubble}</div>
            )}
            <div className="flex flex-col items-end">
              <span className="text-[11px] font-black text-white">{opponentUsername}</span>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`text-[10px] font-bold ${currentPlayer === 'O' ? (timeLeft <= 10 ? 'text-red-500' : 'text-cyan-400') : 'text-gray-600'}`}>
                  {currentPlayer === 'O' ? `${timeLeft}s` : t.waiting_turn}
                </span>
                <span className="text-gray-500 text-[8px] uppercase font-bold">{t.time_left}</span>
              </div>
            </div>
            <div className={`relative w-8 h-8 rounded-lg flex items-center justify-center bg-[#0d121c] border ${currentPlayer === 'O' ? 'border-cyan-500 shadow-[0_0_8px_rgba(34,211,238,0.4)]' : 'border-[#2d3748]'} text-lg`}>{opponentAvatar}</div>
          </div>
        </div>

        <div className="w-full relative bg-[#0b0e14] rounded-xl p-1.5 border border-white/5 overflow-hidden shadow-2xl z-10" style={{ aspectRatio: '14/24' }}>
          <div className="w-full h-full grid gap-[1px] rounded-lg overflow-hidden" style={{ gridTemplateColumns: `repeat(${BOARD_COLS}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${BOARD_ROWS}, minmax(0, 1fr))` }}>
            {board.map((cell, index) => {
              const isWinningCell = winningCells.includes(index);
              const isLastMove = lastMoveIndex === index;
              const isWhiteTheme = boardTheme === 'white';
              const cellBgClass = isWhiteTheme
                ? (isWinningCell ? 'bg-purple-100/90' : 'bg-white hover:bg-purple-50')
                : (isWinningCell ? 'bg-[#2d3a56] ring-1 ring-yellow-400/50 z-10' : 'bg-[#1b2234] hover:bg-[#252e42]');
              return (
                <div key={index} onClick={() => handleCellClick(index)} className={`relative w-full aspect-square cursor-pointer transition-colors duration-100 rounded-[3px] ${cellBgClass}`}>
                  <div className="absolute inset-0 flex items-center justify-center p-[15%]">
                    {cell === 'X' && <GlowingX />}
                    {cell === 'O' && <GlowingO />}
                  </div>
                  {isLastMove && !isWinningCell && (
                    <div className={`absolute inset-0 rounded-[3px] ${isWhiteTheme ? 'bg-purple-500/10' : 'bg-white/5 border border-white/10'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {realWinner && winLineCoords && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-20 overflow-visible" style={{ padding: '0.3rem' }}>
              <line x1={winLineCoords.x1} y1={winLineCoords.y1} x2={winLineCoords.x2} y2={winLineCoords.y2} stroke="#FCD34D" strokeWidth="5" strokeLinecap="round" className="animate-pulse" style={{ filter: 'drop-shadow(0px 0px 8px #F59E0B)' }} />
            </svg>
          )}

          {showWinnerModal && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/85 backdrop-blur-sm rounded-xl animate-in fade-in duration-500">
              <div className="bg-[#171f30] border border-white/10 p-6 rounded-2xl shadow-2xl text-center max-w-xs mx-4">
                <span className="text-[10px] text-yellow-400 font-bold uppercase tracking-widest">{t.win_alert}</span>
                <h3 className="text-xl font-black text-white mt-1 mb-2 uppercase tracking-wide">
                  {realWinner === 'Egalite' ? t.draw : `${realWinner === 'X' ? myUsername : opponentUsername} ${t.win_msg}`}
                </h3>
                {realWinner !== 'Egalite' && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 px-3 py-1 rounded-lg mb-4 inline-block text-xs">
                    <span className="text-yellow-400 font-black">{t.payout}: +{safeToFixed(realWinner === 'X' ? winnerGets : 0)} DOMIA</span>
                  </div>
                )}
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => { if (gameMode === 'ai') setShowBetModal(true); else confirmAndStart(); }}
                    className="flex-1 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-bold shadow-lg hover:opacity-90 transition-opacity uppercase text-xs"
                  >
                    {t.play_again}
                  </button>
                  <button
                    onClick={() => { setRealWinner(null); setShowWinnerModal(false); setView('menu'); }}
                    className="flex-1 py-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-lg font-medium transition-colors uppercase text-xs"
                  >
                    {t.menu}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWaitingOverlay = () => {
    if (!waitingForOpponent) return null;
    const code = ws.joinedRoom?.id || roomCode;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm px-4 animate-in fade-in duration-300" data-testid="ttt-waiting-overlay">
        <div className="bg-[#171f30] border border-cyan-500/30 w-full max-w-sm rounded-2xl shadow-2xl p-6 text-center space-y-4">
          <div className="w-12 h-12 mx-auto rounded-full border-4 border-cyan-500/30 border-t-cyan-400 animate-spin" />
          <h3 className="text-lg font-black text-white uppercase tracking-wider">
            {lang === 'fr' ? "En attente d'un adversaire…" : lang === 'en' ? 'Waiting for opponent…' : 'Esperando oponente…'}
          </h3>
          {code && (
            <div className="bg-black/40 p-3 rounded-xl border border-cyan-500/30">
              <span className="text-[10px] text-cyan-400 font-bold uppercase tracking-widest block mb-1">{t.room_code}</span>
              <div className="flex items-center justify-center gap-2">
                <span className="text-2xl font-mono font-black tracking-widest text-white">{code}</span>
                <button onClick={() => { try { navigator.clipboard.writeText(String(code)); } catch (e) { /* ignore */ } }} className="p-1 hover:bg-white/10 rounded text-gray-400">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">{t.share_code}</p>
            </div>
          )}
          <button
            onClick={async () => {
              try { await ws.leaveRoom(); } catch (e) { /* ignore */ }
              setWaitingForOpponent(false);
              setGameMode(null);
            }}
            className="w-full mt-2 py-2.5 rounded-lg bg-red-600/20 border border-red-500/30 text-red-300 font-bold text-xs uppercase hover:bg-red-600/40"
          >
            {t.cancel}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0d121c] text-white font-sans selection:bg-orange-500/30 overflow-hidden relative" data-testid="tictactoe-game">
      <ParticleBackground />
      {renderHeader()}
      {renderFeaturePopup()}
      {renderJoinModal()}
      {renderBetModal()}
      {renderQuitModal()}
      {renderWaitingOverlay()}
      <main className="relative h-[calc(100vh-69px)] overflow-y-auto overflow-x-hidden scrollbar-hide">
        {view === 'menu' && renderMainMenu()}
        {view === 'tournaments' && renderTournamentsView()}
        {view === 'playing' && renderGameArea()}
      </main>
      {view !== 'playing' && renderLobbyTabBar()}

      {/* === GET READY 3-2-1 overlay (PvP) === */}
      {ws.countdown && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm pointer-events-none" data-testid="ttt-get-ready-overlay">
          <div className="text-center">
            <div className="text-[14px] sm:text-base font-extrabold tracking-[0.4em] text-cyan-300 uppercase mb-4 animate-pulse">Get Ready</div>
            <div
              key={ws.countdown}
              className="text-[140px] sm:text-[220px] font-black leading-none bg-gradient-to-b from-cyan-200 to-blue-600 bg-clip-text text-transparent drop-shadow-[0_0_45px_rgba(59,130,246,0.65)]"
              style={{ animation: 'tttReadyPop 1s ease-out both' }}
            >{ws.countdown}</div>
            <style>{`
              @keyframes tttReadyPop {
                0%   { transform: scale(0.4); opacity: 0; }
                40%  { transform: scale(1.15); opacity: 1; }
                80%  { transform: scale(1); opacity: 1; }
                100% { transform: scale(0.96); opacity: 0.92; }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}
